import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Phone, Video, PhoneOff, Loader2 } from 'lucide-react'
import { useJoinCall, useRejectCall } from '@/hooks/useVideoCall'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import type { IncomingCallData } from '@/services/video-call'

interface IncomingCallModalProps {
  isOpen: boolean
  callData?: IncomingCallData
  onClose: () => void
  onCallAccepted?: (callData: { callId: string; meetingId: string; roomId: string; token: string }) => void
  onCallRejected?: () => void
}

export const IncomingCallModal: React.FC<IncomingCallModalProps> = ({
  isOpen,
  callData,
  onClose,
  onCallAccepted,
  onCallRejected,
}) => {
  const [isAccepting, setIsAccepting] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)
  const [duration, setDuration] = useState(0)
  const [audioEnabled, setAudioEnabled] = useState(false)
  const [audioError, setAudioError] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement>(null)
  const audioContextRef = useRef<AudioContext | null>(null)

  const joinCall = useJoinCall({
    onSuccess: (data) => {
      setIsAccepting(false)
      onCallAccepted?.(data)
      onClose()
    },
    onError: () => {
      setIsAccepting(false)
    },
  })

  const rejectCall = useRejectCall({
    onSuccess: () => {
      setIsRejecting(false)
      onCallRejected?.()
      onClose()
    },
    onError: () => {
      setIsRejecting(false)
    },
  })

  // Audio management functions
  const initializeAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      try {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)()
        console.log('🔊 AudioContext initialized')
      } catch (error) {
        console.error('Failed to initialize AudioContext:', error)
        setAudioError('AudioContext not supported')
      }
    }
  }, [])

  const enableAudioWithUserGesture = useCallback(() => {
    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume().then(() => {
        console.log('🔊 AudioContext resumed')
        setAudioEnabled(true)
      }).catch((error) => {
        console.error('Failed to resume AudioContext:', error)
        setAudioError('Failed to enable audio')
      })
    } else {
      setAudioEnabled(true)
    }
  }, [])

  const createRingtone = useCallback(() => {
    if (!audioContextRef.current) return null
    
    try {
      const oscillator = audioContextRef.current.createOscillator()
      const gainNode = audioContextRef.current.createGain()
      
      oscillator.connect(gainNode)
      gainNode.connect(audioContextRef.current.destination)
      
      // Create a pleasant ringtone pattern
      oscillator.frequency.setValueAtTime(800, audioContextRef.current.currentTime)
      oscillator.frequency.setValueAtTime(600, audioContextRef.current.currentTime + 0.5)
      
      gainNode.gain.setValueAtTime(0.3, audioContextRef.current.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + 1)
      
      return { oscillator, gainNode }
    } catch (error) {
      console.error('Failed to create ringtone:', error)
      return null
    }
  }, [])

  const playRingtone = useCallback(() => {
    console.log('🔊 Attempting to play ringtone...')
    
    // Try HTML5 audio first
    if (audioRef.current && audioEnabled) {
      audioRef.current.volume = 0.5
      const playPromise = audioRef.current.play()
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('🔊 HTML5 audio playing successfully')
          })
          .catch((error) => {
            console.warn('🔊 HTML5 audio failed, trying Web Audio API:', error)
            setAudioError('HTML5 audio blocked: ' + error.message)
            
            // Fallback to Web Audio API
            if (audioContextRef.current && audioEnabled) {
              const ringtone = createRingtone()
              if (ringtone) {
                ringtone.oscillator.start()
                ringtone.oscillator.stop(audioContextRef.current.currentTime + 1)
                console.log('🔊 Web Audio API ringtone playing')
              }
            }
          })
      }
    } else {
      console.warn('🔊 Audio not enabled or ref not available')
      setAudioError('Audio not enabled - user interaction required')
    }
  }, [audioEnabled, createRingtone])

  const stopRingtone = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
  }, [])

  // Initialize audio on mount
  useEffect(() => {
    initializeAudioContext()
    
    // Add event listeners for user interaction
    const handleUserInteraction = () => {
      enableAudioWithUserGesture()
      // Remove listeners after first interaction
      document.removeEventListener('click', handleUserInteraction)
      document.removeEventListener('keydown', handleUserInteraction)
      document.removeEventListener('touchstart', handleUserInteraction)
    }
    
    document.addEventListener('click', handleUserInteraction)
    document.addEventListener('keydown', handleUserInteraction) 
    document.addEventListener('touchstart', handleUserInteraction)
    
    return () => {
      document.removeEventListener('click', handleUserInteraction)
      document.removeEventListener('keydown', handleUserInteraction)
      document.removeEventListener('touchstart', handleUserInteraction)
    }
  }, [initializeAudioContext, enableAudioWithUserGesture])

  // Timer and sound effect
  useEffect(() => {
    if (!isOpen) {
      setDuration(0)
      setAudioError(null)
      stopRingtone()
      return
    }

    console.log('🔔 Incoming call modal opened, starting timer and ringtone')

    // Start timer
    const timer = setInterval(() => {
      setDuration((prev) => prev + 1)
    }, 1000)

    // Start ringtone with a slight delay to allow modal to render
    const ringtoneTimeout = setTimeout(() => {
      playRingtone()
    }, 500)

    return () => {
      clearInterval(timer)
      clearTimeout(ringtoneTimeout)
      stopRingtone()
    }
  }, [isOpen, playRingtone, stopRingtone])

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  if (!isOpen || !callData) return null

  const handleAcceptCall = async () => {
    console.log('✅ Accepting call, stopping ringtone')
    enableAudioWithUserGesture() // Enable audio for future calls
    stopRingtone()
    
    setIsAccepting(true)
    try {
      await joinCall.mutateAsync({
        callId: callData.callId,
      })
    } catch (error) {
      console.error('Failed to accept call:', error)
      setIsAccepting(false)
    }
  }

  const handleRejectCall = async () => {
    console.log('❌ Rejecting call, stopping ringtone')
    enableAudioWithUserGesture() // Enable audio for future calls
    stopRingtone()
    
    setIsRejecting(true)
    try {
      await rejectCall.mutateAsync({
        callId: callData.callId,
        reason: 'rejected_by_counselor',
      })
    } catch (error) {
      console.error('Failed to reject call:', error)
      setIsRejecting(false)
    }
  }

  const getInitials = (name: string) => {
    if (!name) return ''
    return name
      .split(' ')
      .map((n) => n.charAt(0))
      .join('')
      .toUpperCase()
  }

  const caller = callData.from
  const CallIcon = callData.callType === 'video' ? Video : Phone
  const isLoading = isAccepting || isRejecting || joinCall.isPending || rejectCall.isPending

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-90 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 backdrop-blur-md border border-gray-700 rounded-3xl p-8 w-full max-w-sm mx-auto text-center animate-in slide-in-from-bottom-4 duration-300">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-gray-800 text-gray-300 border-gray-600">
              {callData.callType === 'video' ? 'Video' : 'Audio'} Call
            </Badge>
            {!audioEnabled && (
              <Badge variant="outline" className="bg-yellow-800 text-yellow-300 border-yellow-600 text-xs">
                Click to enable sound
              </Badge>
            )}
          </div>
          <div className="text-sm text-gray-400 font-mono">
            {formatDuration(duration)}
          </div>
        </div>

        {/* Caller Avatar */}
        <div className="relative mb-8">
          <div className="relative w-32 h-32 mx-auto">
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 animate-pulse opacity-20"></div>
            <div className="absolute inset-1 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 animate-ping opacity-30"></div>
            <Avatar className="w-32 h-32 border-4 border-white/20 ring-4 ring-blue-500/30">
              <AvatarImage src={caller.avatar} alt={caller.fullName} />
              <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-2xl font-bold">
                {getInitials(caller.fullName)}
              </AvatarFallback>
            </Avatar>
          </div>
          <div className="absolute -bottom-2 -right-2 bg-gray-800 rounded-full p-3 border-2 border-gray-600">
            <CallIcon className="w-6 h-6 text-blue-400" />
          </div>
        </div>

        {/* Caller Info */}
        <div className="mb-8">
          <h3 className="text-2xl font-semibold text-white mb-2">
            {caller.fullName}
          </h3>
          <p className="text-gray-400">
            Incoming {callData.callType} call
          </p>
          {audioError && (
            <p className="text-xs text-yellow-400 mt-2 px-4 py-1 bg-yellow-900/20 rounded">
              {audioError}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center space-x-6">
          <button
            onClick={handleRejectCall}
            disabled={isLoading}
            className="w-16 h-16 bg-red-500 hover:bg-red-600 disabled:opacity-50 rounded-full flex items-center justify-center transition-all duration-200 shadow-lg hover:shadow-xl disabled:cursor-not-allowed"
          >
            {isRejecting ? (
              <Loader2 className="w-6 h-6 text-white animate-spin" />
            ) : (
              <PhoneOff className="w-6 h-6 text-white" />
            )}
          </button>

          <button
            onClick={handleAcceptCall}
            disabled={isLoading}
            className="w-16 h-16 bg-green-500 hover:bg-green-600 disabled:opacity-50 rounded-full flex items-center justify-center transition-all duration-200 shadow-lg hover:shadow-xl disabled:cursor-not-allowed"
          >
            {isAccepting ? (
              <Loader2 className="w-6 h-6 text-white animate-spin" />
            ) : (
              <Phone className="w-6 h-6 text-white" />
            )}
          </button>
        </div>
      </div>

      {/* Audio element for ringtone */}
      <audio
        ref={audioRef}
        loop
        preload="auto"
        muted={false}
        className="hidden"
        onCanPlay={() => console.log('🔊 Audio can play')}
        onError={(e) => {
          console.error('🔊 Audio error:', e)
          setAudioError('Audio file error - check if ringtone.mp3 exists')
        }}
        onLoadedData={() => console.log('🔊 Audio loaded successfully')}
        onPlay={() => console.log('🔊 Ringtone started playing')}
        onPause={() => console.log('🔊 Ringtone paused')}
      >
        <source src="/ringtone.mp3" type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>
    </div>
  )
}